//src/pages/gro/ActionPlanModelDetailPage.tsx
import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronLeft, Pencil, Plus, Save, XCircle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useActionPlanModelDetail,
  useCreateActionPlanModelItem,
  useUpdateActionPlanModelItem,
} from "@/hooks/useActionPlanModels";
import { PERMISSIONS } from "@/lib/permissions";
import { groWritePermission, type GroCrudScope } from "@/lib/groCrudScope";
import type { ActionPlanModelItem, ActionPlanModelItemCreateIn } from "@/types/actionPlanModels";

const emptyItem: ActionPlanModelItemCreateIn = {
  title: "",
  description: "",
  priority: "medium",
  sort_order: 0,
  default_start_offset_days: null,
  default_due_offset_days: null,
  expected_result: "",
  is_active: true,
};

function priorityLabel(priority: string) {
  if (priority === "critical") return "Crítica";
  if (priority === "high") return "Alta";
  if (priority === "low") return "Baixa";
  return "Média";
}

type ActionPlanModelDetailPageProps = {
  scope?: GroCrudScope;
  basePath?: string;
};

export default function ActionPlanModelDetailPage({
  scope = "client",
  basePath = "/gro/action-plan-models",
}: ActionPlanModelDetailPageProps) {
  const navigate = useNavigate();
  const writePermission = groWritePermission(scope, PERMISSIONS.ACTION_PLANS_WRITE);
  const { id } = useParams<{ id: string }>();
  const detailQ = useActionPlanModelDetail(id, scope);
  const createItemMut = useCreateActionPlanModelItem(scope);
  const updateItemMut = useUpdateActionPlanModelItem(scope);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ActionPlanModelItem | null>(null);
  const [itemForm, setItemForm] = useState<ActionPlanModelItemCreateIn>(emptyItem);
  const [itemStatusFilter, setItemStatusFilter] = useState<"active" | "inactive" | "all">("active");

  useEffect(() => {
    if (!dialogOpen) {
      setEditingItem(null);
      setItemForm(emptyItem);
    }
  }, [dialogOpen]);

  function openCreate() {
    setEditingItem(null);
    setItemForm({ ...emptyItem, sort_order: detailQ.data?.items?.length ?? 0 });
    setDialogOpen(true);
  }

  function openEdit(item: ActionPlanModelItem) {
    setEditingItem(item);
    setItemForm({
      title: item.title,
      description: item.description ?? "",
      priority: item.priority,
      sort_order: item.sort_order,
      default_start_offset_days: item.default_start_offset_days ?? null,
      default_due_offset_days: item.default_due_offset_days ?? null,
      expected_result: item.expected_result ?? "",
      is_active: item.is_active,
    });
    setDialogOpen(true);
  }

  async function saveItem() {
    if (!id || !itemForm.title.trim()) {
      toast.error("Informe o título da ação.");
      return;
    }

    try {
      if (editingItem) {
        await updateItemMut.mutateAsync({ itemId: editingItem.id, modelId: id, body: itemForm });
        toast.success("Ação atualizada com sucesso.");
      } else {
        await createItemMut.mutateAsync({ modelId: id, body: itemForm });
        toast.success("Ação cadastrada com sucesso.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Não foi possível salvar a ação.");
    }
  }

  async function toggleItemStatus(item: ActionPlanModelItem, next: boolean) {
    if (!id) return;
    try {
      await updateItemMut.mutateAsync({
        itemId: item.id,
        modelId: id,
        body: {
          title: item.title,
          description: item.description ?? "",
          priority: item.priority,
          sort_order: item.sort_order,
          default_start_offset_days: item.default_start_offset_days ?? null,
          default_due_offset_days: item.default_due_offset_days ?? null,
          expected_result: item.expected_result ?? "",
          is_active: next,
        },
      });
      toast.success(next ? "Ação ativada com sucesso." : "Ação inativada com sucesso.");
    } catch {
      toast.error("Não foi possível alterar o status da ação.");
    }
  }

  const model = detailQ.data;

  const filteredItems = useMemo(() => {
    const items = model?.items ?? [];
    if (itemStatusFilter === "all") return items;
    return items.filter((item) => item.is_active === (itemStatusFilter === "active"));
  }, [model?.items, itemStatusFilter]);

  if (!model) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={model.title}
        description={model.description || "Plano reutilizável para uso posterior nas regras e no inventário."}
      >
        <div className="flex flex-nowrap items-center gap-2">
          <Button variant="outline" onClick={() => navigate(basePath)}>
            <ChevronLeft className="mr-2 h-4 w-4" /> Voltar
          </Button>
          <PermissionButton variant="outline" permission={writePermission} onClick={() => navigate(`${basePath}/${model.id}/editar`)}>
            <Pencil className="mr-2 h-4 w-4" /> Editar plano
          </PermissionButton>
          <PermissionButton permission={writePermission} onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" /> Nova ação
          </PermissionButton>
        </div>
      </PageHeader>

      <Card>
        <CardHeader><CardTitle>Resumo</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          <div><div className="text-sm text-muted-foreground">Código</div><div className="font-medium">{model.code || "-"}</div></div>
          <div><div className="text-sm text-muted-foreground">Plano de Ação</div><div className="font-medium">{model.title || "-"}</div></div>
          <div><div className="text-sm text-muted-foreground">Status</div><div>{model.is_active ? <Badge>Ativo</Badge> : <Badge variant="outline">Inativo</Badge>}</div></div>
          <div><div className="text-sm text-muted-foreground">Qtd. de ações</div><div className="font-medium">{model.items.length}</div></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Ações do plano</CardTitle>
          <div className="w-full sm:w-[220px]">
            <Select value={itemStatusFilter} onValueChange={(value) => setItemStatusFilter(value as typeof itemStatusFilter)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativas</SelectItem>
                <SelectItem value="inactive">Inativas</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filteredItems.length === 0 ? (
            <EmptyState
              title={itemStatusFilter === "inactive" ? "Nenhuma ação inativa" : "Nenhuma ação cadastrada"}
              description={
                itemStatusFilter === "inactive"
                  ? "Este plano ainda não possui ações inativas."
                  : itemStatusFilter === "all"
                    ? "Este plano ainda não possui ações cadastradas."
                    : "Adicione a primeira task/ação deste plano reutilizável."
              }
              actionLabel={itemStatusFilter === "active" || itemStatusFilter === "all" ? "Nova ação" : undefined}
              actionPermission={writePermission}
              onAction={itemStatusFilter === "active" || itemStatusFilter === "all" ? openCreate : undefined}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ordem</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Prioridade</TableHead>
                  <TableHead>Início (dias)</TableHead>
                  <TableHead>Entrega (dias)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.sort_order}</TableCell>
                    <TableCell>{item.title}</TableCell>
                    <TableCell>{priorityLabel(item.priority)}</TableCell>
                    <TableCell>{item.default_start_offset_days ?? "-"}</TableCell>
                    <TableCell>{item.default_due_offset_days ?? "-"}</TableCell>
                    <TableCell>{item.is_active ? <Badge>Ativa</Badge> : <Badge variant="outline">Inativa</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <PermissionButton size="sm" variant="outline" permission={writePermission} onClick={() => openEdit(item)}>
                          <Pencil className="mr-2 h-4 w-4" /> Editar
                        </PermissionButton>
                        {item.is_active ? (
                          <PermissionButton size="sm" variant="outline" permission={writePermission} onClick={() => toggleItemStatus(item, false)}>
                            <XCircle className="mr-2 h-4 w-4" /> Inativar
                          </PermissionButton>
                        ) : (
                          <PermissionButton size="sm" variant="outline" permission={writePermission} onClick={() => toggleItemStatus(item, true)}>
                            <CheckCircle2 className="mr-2 h-4 w-4" /> Ativar
                          </PermissionButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar ação" : "Nova ação"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Título</Label>
              <Input value={itemForm.title} onChange={(e) => setItemForm((prev) => ({ ...prev, title: e.target.value }))} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Descrição</Label>
              <Textarea value={itemForm.description ?? ""} onChange={(e) => setItemForm((prev) => ({ ...prev, description: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Prioridade</Label>
              <Select value={itemForm.priority} onValueChange={(value) => setItemForm((prev) => ({ ...prev, priority: value as typeof prev.priority }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="critical">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ordem</Label>
              <Input type="number" value={itemForm.sort_order} onChange={(e) => setItemForm((prev) => ({ ...prev, sort_order: Number(e.target.value || 0) }))} />
            </div>
            <div className="space-y-2">
              <Label>Início sugerido (dias)</Label>
              <Input type="number" value={itemForm.default_start_offset_days ?? ""} onChange={(e) => setItemForm((prev) => ({ ...prev, default_start_offset_days: e.target.value === "" ? null : Number(e.target.value) }))} />
            </div>
            <div className="space-y-2">
              <Label>Entrega sugerida (dias)</Label>
              <Input type="number" value={itemForm.default_due_offset_days ?? ""} onChange={(e) => setItemForm((prev) => ({ ...prev, default_due_offset_days: e.target.value === "" ? null : Number(e.target.value) }))} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Resultado esperado</Label>
              <Textarea value={itemForm.expected_result ?? ""} onChange={(e) => setItemForm((prev) => ({ ...prev, expected_result: e.target.value }))} />
            </div>
            <div className="flex items-center gap-3 md:col-span-2">
              <Switch checked={itemForm.is_active} onCheckedChange={(checked) => setItemForm((prev) => ({ ...prev, is_active: checked }))} />
              <Label>Ação ativa</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <PermissionButton permission={writePermission} onClick={saveItem}>
              <Save className="mr-2 h-4 w-4" /> Salvar
            </PermissionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
